import React, { useState, useEffect, useRef } from 'react';
import './AffiliationCard.css';
import premiumCard from '../../assets/images/Premium card.png';
import premiumback from '../../assets/images/premiumback.png';
import royalCard from '../../assets/images/royal card.png';
import royalback from '../../assets/images/royalback.png';
import { useLanguage } from "../../contexts/LanguageContext";

const AffiliationCard = () => {
  const { translations: t } = useLanguage();
  const [activeCard, setActiveCard] = useState(0);
  const [flippedCard, setFlippedCard] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const intervalRef = useRef(null);

  const cards = [
    {
      id: 1,
      frontImage: premiumCard,
      backContent: {
        title: t.affiliation?.premium?.title || "Premium Card",
        description: t.affiliation?.premium?.description || "Exclusive benefits for our valued members",
        features: [
          t.affiliation?.premium?.features?.support || "Priority customer support",
          t.affiliation?.premium?.features?.discounts || "Exclusive discounts up to 30%",
          t.affiliation?.premium?.features?.shipping || "Free shipping on all orders",
          t.affiliation?.premium?.features?.access || "Early access to new products"
        ],
        backImage: premiumback
      }
    },
    {
      id: 2,
      frontImage: royalCard,
      backContent: {
        title: t.affiliation?.royal?.title || "Royal Card",
        description: t.affiliation?.royal?.description || "Luxury experience for our most distinguished members",
        features: [
          t.affiliation?.royal?.features?.concierge || "24/7 personal concierge",
          t.affiliation?.royal?.features?.packaging || "Luxury gift packaging",
          t.affiliation?.royal?.features?.services || "Complimentary premium services",
          t.affiliation?.royal?.features?.gift || "Annual luxury gift"
        ],
        backImage: royalback
      }
    }
  ];

  // Starts or restarts the auto-scroll interval
  const startAutoScroll = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveCard(prev => (prev + 1) % cards.length);
    }, 5000);
  };

  // Pause auto-scroll when hovering or a card is flipped
  useEffect(() => {
    if (!isHovering && flippedCard === null) {
      startAutoScroll();
    }
    return () => clearInterval(intervalRef.current);
  }, [isHovering, flippedCard]);

  // Navigate to a specific card (e.g. when clicking a dot)
  const handleCardClick = (index) => {
    if (flippedCard === null) {
      setActiveCard(index);
      startAutoScroll();
    }
  };

  // Flip or unflip a card by its ID
  const handleCardFlip = (cardId) => {
    if (flippedCard === cardId) {
      setFlippedCard(null);
    } else {
      setFlippedCard(cardId);
    }
    startAutoScroll();
  };

  // Go to previous card
  const prevCard = () => {
    setFlippedCard(null);
    setActiveCard(prev => (prev - 1 + cards.length) % cards.length);
    startAutoScroll();
  };

  // Go to next card
  const nextCard = () => {
    setFlippedCard(null);
    setActiveCard(prev => (prev + 1) % cards.length);
    startAutoScroll();
  };

  return (
    <section className="affiliation-section" id="affiliation-cards">
      <div className="affiliation-header">
        <h2 className="affiliation-title">
         {t.affiliation?.header?.title || "Elevate Your Experience with the Membership That Fits You"}
        </h2>
        <p className="affiliation-subtitle">
         {t.affiliation?.header?.subtitle || "Select the plan that aligns with your lifestyle and enjoy exclusive benefits tailored to your needs-designed to enrich your journey and deliver a more personalized experience."}
        </p>
      </div>

      {/* Wrapper that holds arrows and the cards slider */}
      <div className="cards-slider-container">
        <button
          className="nav-arrow left-arrow"
          onClick={prevCard}
          aria-label="Previous Card"
        >
          &#10094;
        </button>

        <div className="cards-slider">
          {cards.map((card, index) => (
            <div
              key={card.id}
              className={`card-wrapper 
                ${index === activeCard ? 'active' : ''} 
                ${index < activeCard ? 'left' : 'right'}`}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <div
                className={`card ${flippedCard === card.id ? 'flipped' : ''}`}
                onClick={() => handleCardFlip(card.id)}
              >
                <div className="card-front">
                  <img
                    src={card.frontImage}
                    alt={card.backContent.title}
                    className="card-image"
                  />
                  <button
                    className="learn-more-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardFlip(card.id);
                    }}
                  >
                    Learn More <span className="arrow-icon">→</span>
                  </button>
                </div>

                <div
                  className="affiliation-card-back"
                  style={{
                    background: card.backContent.backImage
                      ? `url(${card.backContent.backImage}) center/cover no-repeat`
                      : card.backContent.background
                  }}
                >
                  <div className="back-content">
                    <h3 className="back-title">{card.backContent.title}</h3>
                    <p className="back-description">
                      {card.backContent.description}
                    </p>
                    <ul className="back-features">
                      {card.backContent.features.map((feature, i) => (
                        <li key={i} className="back-feature-item">
                          <span className="feature-icon">✓</span> {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          className="nav-arrow right-arrow"
          onClick={nextCard}
          aria-label="Next Card"
        >
          &#10095;
        </button>
      </div>

      <div className="cards-dots">
        {cards.map((_, index) => (
          <button
            key={index}
            className={`cards-dot ${index === activeCard ? 'active' : ''}`}
            onClick={() => handleCardClick(index)}
            disabled={flippedCard !== null}
            aria-label={`Go to card ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default AffiliationCard;
